import {
  Component,
  Host,
  Prop,
  h,
  Element,
  Watch,
  Method,
  Event,
  EventEmitter
} from "@stencil/core";

import { hasSlot } from "../../utils/utils";

import {
  StzhDropdownOption,
  StzhDropdownChangeEvent,
  StzhDropdownItemAddEvent,
  StzhDropdownItemRemoveEvent,
  StzhDropdownOptionAddEvent,
  StzhDropdownOptionRemoveEvent,
  StzhDropdownOpenEvent,
  StzhDropdowCloseEvent,
  StzhDropdownFocusEvent,
  StzhDropdownBlurEvent,
  StzhDropdowInitEvent
} from "../../index";

import { StzhDropdownLocalizedText } from './stzh-dropdown.localization';

import { StzhInputDescription } from "../stzh-input/stzh-input-description";

import TomSelect from "../../libraries/tom-select/esm/tom-select.js";
import TomSelectRemoveButton from "../../libraries/tom-select/esm/plugins/remove_button/plugin.js";
import TomSelectCheckboxOptions from "../../libraries/tom-select/esm/plugins/checkbox_options/plugin.js";
import TomSelectInputAutogrow from "../../libraries/tom-select/esm/plugins/input_autogrow/plugin.js";
import TomSelectNoBackspaceDelete from "../../libraries/tom-select/esm/plugins/no_backspace_delete/plugin.js";

const TomSelectInputReadonly = function (_plugin_options) {
  const self = this;

  self.on("initialize", () => {
    const input = self.control_input as HTMLInputElement;
    if (input) input.readOnly = true;
  })
};

const CLASS_FILLED = "stzh-dropdown--is-filled";
const CLASS_FOCUS = "stzh-dropdown--has-focus";

TomSelect.define("remove_button", TomSelectRemoveButton);
TomSelect.define("checkbox_options", TomSelectCheckboxOptions);
TomSelect.define("input_autogrow", TomSelectInputAutogrow);
TomSelect.define("no_backspace_delete", TomSelectNoBackspaceDelete);
TomSelect.define("input_readonly", TomSelectInputReadonly);

let dropdownCounter = 0;

/**
 * @slot label - Slot for label content
 * @slot description - Slot for description
 * @slot description-long - Slot for long description (in popover)
 * @slot icon - Slot for left icon
 * @slot icon-angle - Slot for angle icon
 * @slot error - Slot for error
 */
@Component({
  tag: "stzh-dropdown",
  styleUrl: "stzh-dropdown.scss"
})
export class StzhDropdown {
  /** Available options */
  @Prop() options: StzhDropdownOption[] | string;
  private _options: StzhDropdownOption[];

  /**
   * Option groups that options will be bucketed into.
   * Make sure each object in the array has a property named whatever `optgroupValueField` is set to.
   */
  @Prop() optgroups: any[] | string;
  private _optgroups: any[];

  /**
   * Set selected options. Can be array set with JS or as JSON string.
   * To get the current (user) selected options, use `getItems` method.
   */
  @Prop() items: string[] | string;
  private _items: string[];

  /**
   * An array of property names to analyze when filtering options.
   * Weights can be given to each field to improve search result:
   * `[{"field":"text","weight":2},{"field":"text2","weight":0.5}]`
   */
  @Prop() searchField: string[] | string = ["text"];
  private _searchField: string[];

  /** Whether multiple items option can be selected */
  @Prop({ reflect: true }) multiple: boolean = false;

  /** Show inline (auto width) */
  @Prop({ reflect: true }) inline: boolean = false;

  /** The max number of options to display in the dropdown. Set to `null` for an unlimited number of options. */
  @Prop() maxOptions: number | null = null;

  /** When searching for multiple terms (separated by space), this is the operator used. Can be 'and' or 'or' */
  @Prop() searchConjunction: "and" | "or" = "and";

  /**  If true, any options with a "" value will be treated like a normal option. This defaults to false to accommodate the common <select> practice of having the first empty option to act as a placeholder. */
  @Prop() allowEmptyOption: boolean = false;

  /** Show the dropdown immediately when the control receives focus. */
  @Prop() openOnFocus: boolean = true;

  /** After a selection is made, the dropdown will remain open if in a multi-selection control or will close in a single-selection control. Setting closeAfterSelect to true will force the dropdown to close after selections are made. Setting closeAfterSelect to false will keep the dropdown open after selections are made. */
  @Prop() closeAfterSelect: boolean | null = null;

  /** Invoked when new options should be loaded from the server. Called with the current query string and a callback function to call with the results when they are loaded (or nothing when an error arises). */
  @Prop() load: (query: string, callback: Function) => any[] | null = null;

  /** Minimum of characters required to execute the `load` function */
  @Prop() minCharacters: number = 3;

  /** The number of milliseconds to wait before requesting options from the server or null. If null, throttling is disabled. Useful when loading options dynamically while the user types a search / filter expression. */
  @Prop() loadThrottle: number = 300;

  /** If true, the load function will be called upon control initialization (with an empty search). Alternatively it can be set to 'focus' to call the load function when control receives focus. */
  @Prop() preload: boolean | "focus" = false;

  /** The name of the property to render as an option / item label (not needed when custom rendering functions are defined). */
  @Prop() labelField: string = "text";

  /** The name of the property to use as the value when an item is selected. */
  @Prop() valueField: string = "value";

  /** The name of the property to group items by. */
  @Prop() optgroupField: string = "optgroup";

  /** The name of the property to render as an option group label (not needed when custom rendering functions are defined). */
  @Prop() optgroupLabelField: string = "label";

  /** The name of the option group property that serves as its unique identifier. */
  @Prop() optgroupValueField: string = "value";

  /** If truthy, all optgroups will be displayed in the same order as they were added (by the `$order` property). Otherwise, it will order based on the score of the results in each. */
  @Prop() lockOptgroupOrder: true;

  /** Name of hidden select element */
  @Prop({ reflect: true }) name: string = "";

  /** Initial open state */
  @Prop() open: boolean = false;

  /** Current open state (readonly) */
  @Prop({ reflect: true }) isOpen: boolean = false;

  /** Variant */
  @Prop({ reflect: true }) variant: "default" | "plain" = "default";

  /** Size variant */
  @Prop({ reflect: true }) size: "default" | "small" = "default";

  /** Whether the dropdown is disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Popover placement */
  @Prop({ reflect: true }) popoverPlacement: "bottom" | "bottom-end" | "bottom-center" | "top" | "top-end" | "top-center" = "bottom";

  /** Label */
  @Prop() label: string = "";

  /** Whether label should be visually hidden. */
  @Prop() labelHidden: boolean = false;

  /** Icon (use instead of icon slot) */
  @Prop() icon: string = "";

  /** Angle icon */
  @Prop() iconAngle: string = "angle-down";

  /** Prevent option search */
  @Prop() noSearch: boolean = false;

  /** Prevent backspace delete */
  @Prop() noBackspaceDelete: boolean = false;

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /** Show text in label depending if field is optional/required */
  @Prop({ reflect: true }) showMarker: boolean = false;

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];
  private _error: string[];

  /** Translation strings */
  @Prop() localization: StzhDropdownLocalizedText;

  /** Id for element which describes the input (this will be overwritten if description prop or slot is used, used by stzh-radiogroup) */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  @Watch("options")
  optionsWatcher(newValue: any[] | string) {
    if (typeof newValue === "string") {
      this._options = JSON.parse(newValue);
    } else {
      this._options = newValue;
    }
  }

  @Watch("items")
  itemsWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      this._items = JSON.parse(newValue);
    } else {
      this._items = newValue;
    }
  }

  @Watch("searchField")
  searchFieldWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      this._searchField = JSON.parse(newValue);
    } else {
      this._searchField = newValue;
    }
  }

  @Watch("optgroups")
  optgroupsWatcher(newValue: any[] | string) {
    if (typeof newValue === "string") {
      this._optgroups = JSON.parse(newValue);
    } else {
      this._optgroups = newValue;
    }
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }

    this.invalid = this._error.length > 0;
  }

  @Element() element: HTMLStzhDropdownElement;

  /**
   * Returns the value of the control.
   * If multiple items can be selected,
   * this returns an array, otherwise a string.
  */
  @Method()
  async getValue(): Promise<string | string[]> {
    return this.tomSelect.getValue();
  }

  /** Set new value */
  @Method()
  async setValue(newValue: string | string[], silent: boolean = false) {
    this.tomSelect.setValue(newValue, silent);
  }

  /** Focus select */
  @Method()
  async setFocus() {
    this.tomSelect.focus();
  }

  /** Reset loaded/cached search queries (calls from load property function) */
  @Method()
  async resetLoadedSearches() {
    this.tomSelect.loadedSearches = {};
  }

  /** Return loaded/cached search queries (calls from load property function) */
  @Method()
  async getLoadedSearches() {
    return this.tomSelect.loadedSearches;
  }

  /** Return current selected items */
  @Method()
  async getItems() {
    return this.tomSelect.items;
  }

  /** Load items by calling load property/function */
  @Method()
  async loadItems(query: string) {
    return this.tomSelect.load(query);
  }

  /** Show dropdown popover */
  @Method()
  async show() {
    return this.tomSelect.open();
  }

  /** Hide dropdown popover */
  @Method()
  async hide() {
    return this.tomSelect.close();
  }

  /** Dropdown change event */
  @Event() stzhChange: EventEmitter<StzhDropdownChangeEvent>;

  /** Dropdown item added event */
  @Event() stzhItemAdd: EventEmitter<StzhDropdownItemAddEvent>;

  /** Dropdown item removed event */
  @Event() stzhItemRemove: EventEmitter<StzhDropdownItemRemoveEvent>;

  /** Dropdown option added event */
  @Event() stzhOptionAdd: EventEmitter<StzhDropdownOptionAddEvent>;

  /** Dropdown option removed event */
  @Event() stzhOptionRemove: EventEmitter<StzhDropdownOptionRemoveEvent>;

  /** Dropdown open event */
  @Event() stzhOpen: EventEmitter<StzhDropdownOpenEvent>;

  /** Dropdown close event */
  @Event() stzhClose: EventEmitter<StzhDropdowCloseEvent>;

  /** Dropdown focus event */
  @Event() stzhFocus: EventEmitter<StzhDropdownFocusEvent>;

  /** Dropdown blur event */
  @Event() stzhBlur: EventEmitter<StzhDropdownBlurEvent>;

  /** Dropdown focus event */
  @Event() stzhInit: EventEmitter<StzhDropdowInitEvent>;

  private tomSelect: TomSelect;
  private select: HTMLSelectElement;
  private rootElement: HTMLElement;
  private dropdownId: string;
  private focusedByInput: boolean = false;

  private updateFilled() {
    if (this._items.length > 0) {
      this.rootElement.classList.add(CLASS_FILLED);
    } else {
      this.rootElement.classList.remove(CLASS_FILLED);
    }
  }

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.setFocus();
    }

    this.focusedByInput = false;
  }

  async componentWillLoad() {
    this.dropdownId = `stzh-dropdown-${dropdownCounter++}`;

    this.optionsWatcher(this.options);
    this.optgroupsWatcher(this.optgroups);
    this.itemsWatcher(this.items);
    this.searchFieldWatcher(this.searchField);
    this.errorWatcher(this.error);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "dropdown");
    }
  }

  componentShouldUpdate(_newValue, _oldValue, prop: string) {
    return ["isOpen"].includes(prop) === false;
  }

  async componentWillUpdate() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }

  async componentDidRender() {
    if (this.tomSelect) {
      return;
    }

    const plugins = {};

    if (this.noSearch) {
      plugins["input_readonly"] = {};
    } else {
      plugins["input_autogrow"] = {};
    }

    if (this.noBackspaceDelete) {
      plugins["no_backspace_delete"] = {};
    }

    if (this.multiple) {
      plugins["checkbox_options"] = {};
      plugins["remove_button"] = {
        title: this.localization.removeButtonTitle,
        label: '<stzh-icon name="close" class="stzh-dropdown__remove-icon"></stzh-icon>'
      };
    }

    const tomSelectOptions: any = {
      plugins,
      maxItems: this.multiple ? null : 1,
      maxOptions: this.maxOptions,
      items: this._items,
      optgroups: this._optgroups,
      searchField: this._searchField,
      searchConjunction: this.searchConjunction,
      allowEmptyOption: this.allowEmptyOption,
      closeAfterSelect: this.closeAfterSelect !== null
        ? this.closeAfterSelect
        : !this.multiple,
      labelField: this.labelField,
      valueField: this.valueField,
      optgroupField: this.optgroupField,
      optgroupLabelField: this.optgroupLabelField,
      optgroupValueField: this.optgroupValueField,
      lockOptgroupOrder: this.lockOptgroupOrder,
      load: this.load,
      shouldLoad: (query: string) => {
        return query.length >= this.minCharacters;
      },
      loadThrottle: this.loadThrottle,
      preload: this.preload,
      openOnFocus: this.openOnFocus,
      loadingClass: "is-loading",
      hideSelected: false,
      duplicates: false,
      delimiter: ",",
      copyClassesToDropdown: false,
      hidePlaceholder: null,
      // if you want the user to be able to add own items enable this option
      // (but checkbox_options plugin throws an error and would need a fix first)
      // create: function(input, callback) {
      //   callback({ value: input, text: input });
      // },
      // createOnBlur: false,
      // createFilter: null,
      // persist: false,
      render: {
        option: (data, escape) => {
          return `
            <div class="stzh-dropdown__option">
              <stzh-icon name="checkmark" class="stzh-dropdown__option-check"></stzh-icon>
              <span class="stzh-dropdown__option-title">
                ${escape(data[this.labelField])}
              </span>
            </div>
          `;
        },
        item: (data, escape) => {
          return `
            <div class="stzh-dropdown__item">
              ${escape(data[this.labelField])}
            </div>
          `;
        },
        option_create: (data, escape) => {
          return `
            <div class="stzh-dropdown__create">
              ${this.localization.create.replace(/\{x\}/gi, escape(data.input))}&hellip;
            </div>
          `;
        },
        no_results: (data, escape) => {
          return `
            <div class="stzh-dropdown__no-results" id="${this.dropdownId}-no-results">
              ${this.localization.noResults.replace(/\{x\}/gi, escape(data.input))}
            </div>`;
        },
        not_loading: this.load === null ? null : (_data, escape) => {
          return `
            <div class="stzh-dropdown__not-loading" id="${this.dropdownId}-not-loading">
              ${this.localization.notLoading.replace(/\{x\}/gi, escape(this.minCharacters.toString()))}
            </div>
          `;
        },
        optgroup: (data) => {
          let optgroup = document.createElement("div");
          optgroup.className = "stzh-dropdown__optgroup";
          optgroup.appendChild(data.options);
          return optgroup;
        },
        optgroup_header: (data, escape) => {
          return `
            <div class="stzh-dropdown__optgroup-header">
              ${escape(data.label)}
            </div>
          `;
        },
        loading: () => {
          return `
            <div class="stzh-dropdown__loader">
              <stzh-loader></stzh-loader>
            </div>
          `;
        },
        dropdown: () => {
          return `<div class="stzh-dropdown__dropdown"></div>`;
        }
      },
      onChange: (value) => {
        this._items = this.tomSelect.items;
        this.updateFilled();

        this.stzhChange.emit({
          component: "stzh-dropdown",
          value
        });
      },
      onItemAdd: (value) => {
        this._items = this.tomSelect.items;
        this.stzhItemAdd.emit({
          component: "stzh-dropdown",
          value
        });
      },
      onItemRemove: (value) => {
        this._items = this.tomSelect.items;
        this.stzhItemRemove.emit({
          component: "stzh-dropdown",
          value
        });
      },
      onOptionAdd: (value) => {
        this._options = this.tomSelect.options;
        this.updateFilled();

        this.stzhOptionAdd.emit({
          component: "stzh-dropdown",
          value
        });
      },
      onOptionRemove: (value) => {
        this._options = this.tomSelect.options;
        this.updateFilled();

        this.stzhOptionRemove.emit({
          component: "stzh-dropdown",
          value
        });
      },
      onDropdownOpen: () => {
        this.isOpen = true;
        this.stzhOpen.emit({
          component: "stzh-dropdown"
        });
      },
      onDropdownClose: () => {
        this.isOpen = false;
        this.stzhClose.emit({
          component: "stzh-dropdown"
        });
      },
      onFocus: () => {
        this.focusedByInput = true;

        if (this.tomSelect.load && this.preload === "focus") {
          // to be able to execute load function again, we reset loaded searches
          this.resetLoadedSearches();

          // if results have been preloaded before, load them now directly with load function
          if (this.tomSelect.wrapper.classList.contains('preloaded')) {
            // since tomSelect.preload() doesn't call tomSelect.shouldCall() method internally (to check for minCharacters),
            // we will also not call it (this.tomSelect.settings.shouldLoad.call(this.tomSelect, ''))
            this.tomSelect.load('');
          }
        }

        this.rootElement.classList.add(CLASS_FOCUS);
        const focusEvent = new FocusEvent('focus', {
          view: window,
          bubbles: false,
          cancelable: false
        });

        this.element.dispatchEvent(focusEvent);
        this.stzhFocus.emit({
          component: "stzh-dropdown",
          originalEvent: {
            target: this.tomSelect.control
          }
        });
      },
      onBlur: () => {
        this.rootElement.classList.remove(CLASS_FOCUS);
        const blurEvent = new FocusEvent('blur', {
          view: window,
          bubbles: false,
          cancelable: false
        });

        this.element.dispatchEvent(blurEvent);
        this.stzhBlur.emit({
          component: "stzh-dropdown"
        });
      },
      onInitialize: () => {
        this.stzhInit.emit({
          component: "stzh-dropdown"
        });
      },
      onLoad: () => {
        this._options = this.tomSelect.options;
      }
    };

    if (this._options) {
      tomSelectOptions.options = this._options;
    }

    if (this._optgroups) {
      tomSelectOptions.optgroups = this._optgroups;
    }

    if (this._items) {
      tomSelectOptions.items = this._items;
    }

    this.tomSelect = new TomSelect(this.select, tomSelectOptions);

    if (!this.openOnFocus) {
      // we still want to enable opening the dropdown by click
      this.tomSelect.control.addEventListener("click", () => {
        this.tomSelect.open();
      });
    }

    if (this.open) {
      this.show();
    }
  }

  disconnectedCallback() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }

  render() {
    const iconUsed = hasSlot(this.element, 'icon') || !!this.icon;
    const descriptionUsed = hasSlot(this.element, 'description') || !!this.description;
    const descriptionLongUsed = hasSlot(this.element, 'description-long') || !!this.descriptionLong;
    const errorUsed = hasSlot(this.element, 'error') || !!this.error;

    const classes = {
      "stzh-dropdown": true,
      "stzh-dropdown--is-inline": this.inline,
      "stzh-dropdown--is-multiple": this.multiple,
      "stzh-dropdown--is-disabled": this.disabled,
      "stzh-dropdown--is-required": this.required,
      "stzh-dropdown--is-invalid": this.invalid,
      [CLASS_FILLED]: this._items && this._items.length > 0,
      "stzh-dropdown--has-icon": iconUsed,
      "stzh-dropdown--has-description": descriptionUsed,
      "stzh-dropdown--has-description-long": descriptionLongUsed,
      "stzh-dropdown--has-error": errorUsed,
      "stzh-dropdown--has-no-search": this.noSearch,
      "stzh-dropdown--has-fixed-label": window?.stzhComponents?.labels === "fixed",
      "stzh-dropdown--has-label-hidden": this.labelHidden,
      [`stzh-dropdown--popover-placement-${this.popoverPlacement}`]: !!this.popoverPlacement,
      [`stzh-dropdown--size-${this.size}`]: !!this.size,
      [`stzh-dropdown--${this.variant}`]: !!this.variant
    };

    return (
      <Host tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        <div
          class={classes}
          ref={(el) => (this.rootElement = el as HTMLDivElement)}
        >
          <div class="stzh-dropdown__field-wrapper">
            {/* tom-select replaces this select with an combobox.
                Since it doesn't support aria-(describedby/required/invalid),
                we write necessary status information in the aria-labelledby */}
            <select
              id={this.dropdownId}
              class="stzh-dropdown__select"
              ref={(el) => (this.select = el as HTMLSelectElement)}
              multiple={this.multiple}
              name={this.name}
              disabled={this.disabled}
              aria-labelledby={`${this.dropdownId}-label ${this.dropdownId}-not-loading ${this.dropdownId}-no-results ${!this.showMarker && this.required && `${this.dropdownId}-required`} ${this.invalid && `${this.dropdownId}-invalid`} ${this.dropdownId}-description ${this.a11yDescribedby}`}
            >
              <slot></slot>
            </select>

            {/* <div class="stzh-dropdown__sizer">
              {this.placeholder}
            </div> */}

            <div class="stzh-dropdown__icon-angle-wrapper">
              {this.iconAngle ?
                <stzh-icon name={this.iconAngle} class="stzh-dropdown__icon-angle"></stzh-icon>
                :
                <slot name="icon-angle"></slot>
              }
            </div>

            <div class="stzh-dropdown__icon-wrapper">
              {this.icon ?
                <stzh-icon class="stzh-dropdown__icon" name={this.icon}></stzh-icon>
                :
                <slot name="icon"></slot>
              }
            </div>

            <label
              id={`${this.dropdownId}-label`}
              class="stzh-dropdown__label"
              htmlFor={this.dropdownId}
            >
              {this.label ? this.label : <slot name="label"></slot>}
              {this.showMarker &&
                <span class="stzh-dropdown__marker">
                  <span class="stzh-dropdown__marker-symbol" aria-hidden="true">
                    {this.required
                      ? this.localization.$globals.requiredFieldMarker
                      : this.localization.$globals.optionalFieldMarker
                    }
                  </span>
                  <span class="stzh-dropdown__marker-text">
                    {this.required
                      ? this.localization.$globals.requiredFieldText
                      : this.localization.$globals.optionalFieldText
                    }
                  </span>
                </span>
              }
            </label>

            {!this.showMarker && this.required &&
              <div id={`${this.dropdownId}-required`} class="stzh-dropdown__vhidden">
                {this.localization.$globals.requiredFieldText}
              </div>
            }

            {this.invalid &&
              <div id={`${this.dropdownId}-invalid`} class="stzh-dropdown__vhidden">
                {this.localization.$globals.invalidFieldText}
              </div>
            }
          </div>
          <StzhInputDescription
            classPrefix="stzh-dropdown"
            id={`${this.dropdownId}-description`}
            error={this._error}
            description={this.description}
            descriptionLong={this.descriptionLong}
            descriptionLongUsed={descriptionLongUsed}
            moreInfoButtonLabel={this.localization.$globals.moreInfoButtonLabel}
          />
        </div>
      </Host>
    );
  }
}
